import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import { authAPI } from "../services/api";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    language: i18n.language,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\]{};':"\\|,.<>?])[A-Za-z\d!@#$%^&*()_+\-=\]{};':"\\|,.<>?]{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validatePassword(formData.password)) {
      setError(t("auth.register.passwordRequirements"));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t("auth.register.passwordMismatch"));
      return;
    }

    if (!executeRecaptcha) {
      setError(t("auth.register.captchaError"));
      return;
    }

    // Execute reCAPTCHA for the "register" action
    const captchaToken = await executeRecaptcha("register");
    if (!captchaToken) {
      setError(t("auth.register.captchaRequired"));
      return;
    }

    // Build the registration payload explicitly,
    // ensuring language is included from i18n.language.
    const registrationData = {
      username: formData.username,
      email: formData.email,
      password: formData.password,
      language: i18n.language,
      captchaToken,
    };

    try {
      setLoading(true);
      const response = await authAPI.register(registrationData);

      if (response.data.success) {
        navigate("/login", {
          state: {
            message: t("auth.register.verificationSent"),
            type: "success",
          },
        });
      } else {
        setError(response.data.message || t("auth.register.error"));
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.response?.data?.message || t("auth.register.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, width: "100%" }}>
        <Typography variant="h5" gutterBottom align="center">
          {t("auth.register.title")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t("auth.register.username")}
            margin="normal"
            value={formData.username}
            onChange={(e) =>
              setFormData({ ...formData, username: e.target.value })
            }
            disabled={loading}
            required
          />
          <TextField
            fullWidth
            label={t("auth.register.email")}
            type="email"
            margin="normal"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            disabled={loading}
            required
          />
          <TextField
            fullWidth
            label={t("auth.register.password")}
            type="password"
            margin="normal"
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            disabled={loading}
            required
            helperText={t("auth.register.passwordHelp")}
          />
          <TextField
            fullWidth
            label={t("auth.register.confirmPassword")}
            type="password"
            margin="normal"
            value={formData.confirmPassword}
            onChange={(e) =>
              setFormData({ ...formData, confirmPassword: e.target.value })
            }
            disabled={loading}
            required
          />
          <Button
            fullWidth
            variant="contained"
            type="submit"
            sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              t("auth.register.submitButton")
            )}
          </Button>
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            {t("auth.register.alreadyHaveAccount", "Already have an account?")}{" "}
            <Link component={RouterLink} to="/login">
              {t("auth.register.loginHere", "Login here")}
            </Link>
          </Typography>
        </form>
      </Paper>
    </Box>
  );
};

export default Register;
